import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  UserCheck, 
  ArrowLeft, 
  CheckCircle, 
  AlertCircle,
  Heart
} from 'lucide-react';
import { db } from '../../database/supabase';
import { useAuth } from '../../contexts/AuthContext';

const IntakeInterviewForm = () => {
  const { register, handleSubmit, formState: { errors }, watch } = useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    try {
      const { data: formTemplate, error: formError } = await db.getFormByType('intake_interview');
      
      if (formError) {
        throw new Error('Could not load form template');
      }

      const submissionData = {
        form_id: formTemplate.id,
        user_id: user?.id || null,
        data: data,
        status: 'submitted',
        submitted_at: new Date().toISOString()
      };

      const { error: submitError } = await db.createSubmission(submissionData);
      
      if (submitError) {
        throw submitError;
      }

      toast.success('Intake interview submitted successfully!');
      navigate('/forms', { 
        state: { 
          message: 'Your intake interview has been submitted. A counselor will review it and contact you to schedule your first session.' 
        }
      });
      
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom">
        <div className="mb-8">
          <button
            onClick={() => navigate('/forms')}
            className="flex items-center text-blue-800 hover:text-red-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Forms
          </button>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserCheck className="w-8 h-8 text-blue-800" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Intake Interview Form
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              This form helps us understand your background and current situation so we can 
              provide you with the most appropriate counseling support.
            </p>
          </div>
        </div>

        <div className="form-container">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Personal Information */}
            <div className="form-section">
              <h2 className="form-section-title">Personal Information</h2>
              <div className="form-grid">
                <div>
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    {...register('fullName', { required: 'Full name is required' })}
                    placeholder="Enter your full name"
                  />
                  {errors.fullName && (
                    <p className="form-error">{errors.fullName.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Age *</label>
                  <input
                    type="number"
                    className="form-input"
                    {...register('age', { 
                      required: 'Age is required',
                      min: { value: 1, message: 'Please enter a valid age' },
                      max: { value: 100, message: 'Please enter a valid age' }
                    })}
                    placeholder="Enter your age"
                  />
                  {errors.age && (
                    <p className="form-error">{errors.age.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Gender *</label>
                  <select
                    className="form-input"
                    {...register('gender', { required: 'Gender is required' })}
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non-binary">Non-binary</option>
                    <option value="prefer-not-to-say">Prefer not to say</option>
                  </select>
                  {errors.gender && (
                    <p className="form-error">{errors.gender.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Contact Number *</label>
                  <input
                    type="tel"
                    className="form-input"
                    {...register('phone', { required: 'Contact number is required' })}
                    placeholder="Enter your phone number"
                  />
                  {errors.phone && (
                    <p className="form-error">{errors.phone.message}</p>
                  )}
                </div>

                <div className="form-full-width">
                  <label className="form-label">Email Address *</label>
                  <input
                    type="email"
                    className="form-input"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    placeholder="Enter your email address"
                  />
                  {errors.email && (
                    <p className="form-error">{errors.email.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="form-section">
              <h2 className="form-section-title">Emergency Contact Information</h2>
              <div className="form-grid">
                <div>
                  <label className="form-label">Emergency Contact Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    {...register('emergencyContactName', { required: 'Emergency contact name is required' })}
                    placeholder="Enter emergency contact name"
                  />
                  {errors.emergencyContactName && (
                    <p className="form-error">{errors.emergencyContactName.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Relationship *</label>
                  <select
                    className="form-input"
                    {...register('emergencyContactRelationship', { required: 'Relationship is required' })}
                  >
                    <option value="">Select relationship</option>
                    <option value="parent">Parent</option>
                    <option value="guardian">Guardian</option>
                    <option value="sibling">Sibling</option>
                    <option value="spouse">Spouse</option>
                    <option value="friend">Friend</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.emergencyContactRelationship && (
                    <p className="form-error">{errors.emergencyContactRelationship.message}</p>
                  )}
                </div>

                <div className="form-full-width">
                  <label className="form-label">Emergency Contact Phone *</label>
                  <input
                    type="tel"
                    className="form-input"
                    {...register('emergencyContactPhone', { required: 'Emergency contact phone is required' })}
                    placeholder="Enter emergency contact phone number"
                  />
                  {errors.emergencyContactPhone && (
                    <p className="form-error">{errors.emergencyContactPhone.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Reason for Seeking Counseling */}
            <div className="form-section">
              <h2 className="form-section-title">Reason for Seeking Counseling</h2>
              <div className="space-y-6">
                <div>
                  <label className="form-label">
                    What brings you to counseling today? *
                  </label>
                  <textarea
                    className="form-input"
                    rows="4"
                    {...register('reasonForCounseling', { required: 'Please describe your reason for seeking counseling' })}
                    placeholder="Please describe what prompted you to seek counseling services..."
                  />
                  {errors.reasonForCounseling && (
                    <p className="form-error">{errors.reasonForCounseling.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">
                    How long have you been experiencing these concerns?
                  </label>
                  <div className="space-y-2">
                    {[
                      'Less than a week',
                      '1-2 weeks',
                      '1 month',
                      '2-6 months',
                      '6 months - 1 year',
                      'More than 1 year'
                    ].map((duration) => (
                      <label key={duration} className="flex items-center">
                        <input
                          type="radio"
                          value={duration}
                          {...register('concernDuration')}
                          className="mr-3"
                        />
                        {duration}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="form-label">
                    What areas would you like to focus on? (Check all that apply)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      'Academic stress',
                      'Anxiety',
                      'Depression',
                      'Relationship issues',
                      'Family problems',
                      'Self-esteem',
                      'Career concerns',
                      'Social difficulties',
                      'Grief/loss',
                      'Trauma',
                      'Substance use',
                      'Other'
                    ].map((area) => (
                      <label key={area} className="flex items-center">
                        <input
                          type="checkbox"
                          value={area}
                          {...register('focusAreas')}
                          className="mr-3"
                        />
                        {area}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Mental Health History */}
            <div className="form-section">
              <h2 className="form-section-title">Mental Health History</h2>
              <div className="space-y-6">
                <div>
                  <label className="form-label">
                    Have you received counseling or therapy before? *
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="yes"
                        {...register('previousCounseling', { required: 'Please select an option' })}
                        className="mr-3"
                      />
                      Yes
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="no"
                        {...register('previousCounseling')}
                        className="mr-3"
                      />
                      No
                    </label>
                  </div>
                  {errors.previousCounseling && (
                    <p className="form-error">{errors.previousCounseling.message}</p>
                  )}
                </div>

                {watch('previousCounseling') === 'yes' && (
                  <div>
                    <label className="form-label">
                      Please describe your previous counseling experience
                    </label>
                    <textarea
                      className="form-input"
                      rows="3"
                      {...register('previousCounselingDetails')}
                      placeholder="When, where, and what type of counseling did you receive?"
                    />
                  </div>
                )}

                <div>
                  <label className="form-label">
                    Are you currently taking any medications for mental health? *
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="yes"
                        {...register('currentMedications', { required: 'Please select an option' })}
                        className="mr-3"
                      />
                      Yes
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="no"
                        {...register('currentMedications')}
                        className="mr-3"
                      />
                      No
                    </label>
                  </div>
                  {errors.currentMedications && (
                    <p className="form-error">{errors.currentMedications.message}</p>
                  )}
                </div>

                {watch('currentMedications') === 'yes' && (
                  <div>
                    <label className="form-label">
                      Please list your current medications
                    </label>
                    <textarea
                      className="form-input"
                      rows="3"
                      {...register('medicationDetails')}
                      placeholder="List medications, dosages, and prescribing doctor if known"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Support System */}
            <div className="form-section">
              <h2 className="form-section-title">Support System</h2>
              <div className="space-y-6">
                <div>
                  <label className="form-label">
                    Who do you currently talk to about your problems?
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      'Family members',
                      'Friends',
                      'Teachers',
                      'Religious leader',
                      'Healthcare provider',
                      'No one',
                      'Other'
                    ].map((support) => (
                      <label key={support} className="flex items-center">
                        <input
                          type="checkbox"
                          value={support}
                          {...register('supportSystem')}
                          className="mr-3"
                        />
                        {support}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="form-label">
                    How would you describe your current stress level?
                  </label>
                  <div className="space-y-2">
                    {['Very Low', 'Low', 'Moderate', 'High', 'Very High'].map((level) => (
                      <label key={level} className="flex items-center">
                        <input
                          type="radio"
                          value={level}
                          {...register('stressLevel')}
                          className="mr-3"
                        />
                        {level}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="form-label">
                    What coping strategies do you currently use?
                  </label>
                  <textarea
                    className="form-input"
                    rows="3"
                    {...register('copingStrategies')}
                    placeholder="Describe how you currently handle stress or difficult situations..."
                  />
                </div>
              </div>
            </div>

            {/* Goals and Expectations */}
            <div className="form-section">
              <h2 className="form-section-title">Goals and Expectations</h2>
              <div className="space-y-6">
                <div>
                  <label className="form-label">
                    What do you hope to achieve through counseling? *
                  </label>
                  <textarea
                    className="form-input"
                    rows="4"
                    {...register('counselingGoals', { required: 'Please describe your goals' })}
                    placeholder="Describe what you hope to accomplish or change through counseling..."
                  />
                  {errors.counselingGoals && (
                    <p className="form-error">{errors.counselingGoals.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">
                    How motivated are you to make changes in your life?
                  </label>
                  <div className="space-y-2">
                    {[
                      'Very motivated',
                      'Somewhat motivated',
                      'Neutral',
                      'Not very motivated',
                      'Not motivated at all'
                    ].map((level) => (
                      <label key={level} className="flex items-center">
                        <input
                          type="radio"
                          value={level}
                          {...register('motivation')}
                          className="mr-3"
                        />
                        {level}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="form-label">
                    Is there anything else you'd like your counselor to know?
                  </label>
                  <textarea
                    className="form-input"
                    rows="4"
                    {...register('additionalInformation')}
                    placeholder="Share any other information that might be helpful for your counselor to know..."
                  />
                </div>
              </div>
            </div>

            {/* Confidentiality Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Confidentiality Notice</p>
                  <p>
                    All information provided in this intake form is confidential and will only be 
                    shared with your assigned counselor and other authorized personnel as necessary 
                    for your care. Information will only be disclosed without your consent in cases 
                    where there is imminent danger to yourself or others, or as required by law.
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner w-4 h-4 mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Submit Intake Form
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default IntakeInterviewForm;